import { useEffect, useRef, useState } from 'react';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  /** Placeholder shown when nothing selected */
  placeholder: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Dropdown trigger button that opens a checkbox panel for multi-select.
 * Closes on outside click. Selected count shown on the trigger.
 */
export default function MultiSelectDropdown({
  placeholder,
  options,
  value,
  onChange,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  const count = value.length;
  const label = count === 0 ? placeholder : `${placeholder} · ${count}`;

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 border rounded-md text-sm outline-none transition-colors cursor-pointer whitespace-nowrap ${
          count > 0
            ? 'bg-primary-50 border-primary-300 text-primary-700'
            : 'bg-background-100 border-background-200 text-foreground-700 hover:border-background-300'
        }`}
      >
        {label}
        <i className={`ri-arrow-down-s-line transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 left-0 min-w-[12rem] max-h-72 overflow-y-auto bg-background-50 border border-background-200 rounded-md shadow-lg p-1">
          {options.map((opt) => {
            const checked = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors cursor-pointer ${
                  checked ? 'text-primary-700 bg-primary-50' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <span
                  className={`w-4 h-4 flex items-center justify-center rounded border text-xs ${
                    checked ? 'bg-primary-500 border-primary-500 text-background-50' : 'border-background-300'
                  }`}
                >
                  {checked && <i className="ri-check-line"></i>}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
